import { inject, Injectable } from '@angular/core';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { cloneDeep } from 'lodash-es';
import { Subject } from 'rxjs';
import { findFlowchartElementRecursive } from 'shared/domain';
import { initalUniqueId } from 'shared/util-common';

import {
  FlowchartActionDto,
  FlowchartContainerDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  FlowchartNodeType,
  FlowchartSelectionBehaviorDto,
  FlowchartStartNodeDto,
  FlowchartSubflowDto,
  FlowchartTextBlockDto,
  GuidelineActionDto,
  GuidelineComponentType,
  LinkAnchor,
  LinkVertexDto,
  SelectionBehavior,
} from '@treetop/shared/model';

import { FlowchartNode } from '../../entities/FlowchartNode';
import { ConverterService } from '../base/converter.service';
import { FlowchartConfig } from '../base/flowchart.config';
import { LinkLabelsDictionary } from './link/attribute/AttributePositionLogic';
import OperationsService, { LinkAnchorData } from './operations.service';
import { SizeService } from './size.service';
import SubflowService from './subflow.service';
import { FlowchartElementsSubjectStore } from '../data/flowchart-elements-subject-store.service';
import { NodePickerEvent } from './node-picker-toolbar/node-picker-toolbar-items.config';

@Injectable()
export class FlowchartLoaderService {
  private converterService = inject(ConverterService);
  private sizeService = inject(SizeService);
  private sidebarService = inject(LegacySidebarService);
  private translocoService = inject(TranslocoService);

  public unselectFlowchartElmements = new Subject<void>();

  loadFlowchartFromDto(
    flowchartElements: FlowchartNodeDto[],
    operationsService: OperationsService,
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    scroller: joint.ui.PaperScroller,
    subflowService: SubflowService,
    zoomToFit = true,
    allowEvents = false,
  ): void {
    const copiedFlowchartElements = cloneDeep(flowchartElements);
    const flowchartNodes = [];

    copiedFlowchartElements.forEach((element) => {
      if (element.uniqueId == initalUniqueId) {
        element.uniqueId = element.id;
      }

      const flowchartNode = new FlowchartNode(element);
      flowchartNode.addToGraph(graph, paper, operationsService, subflowService, this.translocoService, allowEvents);

      flowchartNodes.push(flowchartNode);
    });

    flowchartNodes.forEach((node) => {
      if (node.element?.outgoingLinks?.length > 0) {
        this.createOutgoingLinks(copiedFlowchartElements, graph, operationsService, node.element.outgoingLinks);
      }

      if (node.element.elementType === FlowchartElementType.FlowchartSubflow) {
        node.element.children?.forEach((subflowNode) => {
          if (subflowNode?.outgoingLinks?.length > 0) {
            this.createOutgoingLinks(copiedFlowchartElements, graph, operationsService, subflowNode.outgoingLinks);
          }
        });
        const parentElement = graph.getCell(node.element.id);
        this.sizeService.resizeParentToFit(parentElement as joint.dia.Element, graph);
      }
    });

    if (zoomToFit) {
      setTimeout(() => {
        scroller.zoomToFit({
          maxScale: 1,
          minScale: FlowchartConfig.paper.ZOOM_MIN,
        });

        const outgoingLinks = flowchartNodes[0].element.outgoingLinks;
        const flowchartBuildFromLeftToRight =
          outgoingLinks?.length > 0 && outgoingLinks.every((link: FlowchartLinkDto) => link.fromFlowchartNodeAnchor === LinkAnchor.Right);
        const position = flowchartBuildFromLeftToRight ? 'left' : 'top';
        const padding = flowchartBuildFromLeftToRight ? { left: 10 } : { top: 100 };
        const startNode = graph.getElements()[0];
        scroller.positionElement(startNode, position, { padding });
        this.sizeService.updatePaperHeight.next(true);

        const scrollToCenter = this.shouldScrollToCenter(paper, scroller);
        if (scrollToCenter) {
          scroller.zoomToFit({
            maxScale: 1,
            minScale: FlowchartConfig.paper.ZOOM_MIN,
            verticalAlign: 'middle',
            horizontalAlign: 'middle',
            padding: 40,
          });
        }
      }, 100);
    }
  }

  shouldScrollToCenter(paper: joint.dia.Paper, scroller: joint.ui.PaperScroller): boolean {
    const paperBbox = paper.getContentBBox({ useModelGeometry: true });
    const nativePaperArea = paper.getArea();

    const { x: scrollerX, y: scrollerY, width: scrollerWidth, height: scrollerHeight } = scroller.getVisibleArea();
    const { x: paperX, y: paperY, width: paperWidth, height: paperHeight } = paperBbox;

    const normalizedScrollerWidth = scrollerWidth * paper.scale().sx;
    const normalizedScrollerHeight = scrollerHeight * paper.scale().sy;

    const normalizedPaperX = paperX / paper.scale().sx;
    const normalizedPaperY = paperY / paper.scale().sy;
    const normalizedPaperWidth = paperWidth / paper.scale().sx;
    const normalizedPaperHeight = paperHeight / paper.scale().sy;

    const normalizedTotalWidth = normalizedPaperWidth * FlowchartConfig.paper.ZOOM_MIN;
    const normalizedTotalHeight = normalizedPaperHeight * FlowchartConfig.paper.ZOOM_MIN;

    if (normalizedTotalWidth > normalizedScrollerWidth) {
      return false;
    }

    if (normalizedTotalHeight > normalizedScrollerHeight) {
      return false;
    }

    if (normalizedPaperX + nativePaperArea.x < scrollerX) {
      return true;
    } else if (normalizedPaperX + normalizedPaperWidth + nativePaperArea.x > scrollerX + scrollerWidth) {
      return true;
    }

    // Unknown +40 (padding)
    if (normalizedPaperY + nativePaperArea.y - 40 < scrollerY) {
      return true;
    } else if (normalizedPaperY + normalizedPaperHeight + nativePaperArea.y - 40 > scrollerY + scrollerHeight) {
      return true;
    }

    return false;
  }

  createOutgoingLinks(
    flowchartElements: FlowchartNodeType[],
    graph: joint.dia.Graph,
    operationsService: OperationsService,
    links: any[],
  ): void {
    const existingLinks = [];
    if (links?.length > 0) {
      links.forEach((link: FlowchartLinkDto) => {
        const sourceElement = graph.getCell(link.fromFlowchartNodeId);
        const targetElement = graph.getCell(link.toFlowchartNodeId);
        const linkId = link.id;
        const sourceAnchor = this.converterService.getAnchorName(link.fromFlowchartNodeAnchor);
        const targetAnchor = this.converterService.getAnchorName(link.toFlowchartNodeAnchor);
        const existingLink = operationsService.createLink(sourceElement, targetElement, sourceAnchor, targetAnchor, linkId);
        if (link.linkVertices && link.linkVertices.length > 0) {
          const vertices = link.linkVertices.map((el: LinkVertexDto) => {
            return { x: el.positionX, y: el.positionY };
          });
          existingLink.set('vertices', vertices);
          if (link.linkVerticesConfig?.isManual) {
            existingLink.set('manual', true);
          }
        }
        existingLinks.push(existingLink);
        graph.addCell(existingLink);
      });

      existingLinks.forEach((existingLink) => {
        this.setLinkValues(flowchartElements, operationsService, existingLink);
      });
    }
  }

  //TODO: check if it has to be moved or if it exits already works for 0.9.19
  flattenFlowchartElements(elements: FlowchartNodeDto[]): FlowchartNodeDto[] {
    let flatArray: FlowchartNodeDto[] = [];

    elements.forEach((node) => {
      flatArray.push(node);

      if (node.children) {
        flatArray = flatArray.concat(this.flattenFlowchartElements(node.children as FlowchartDecisionNodeDto[]));
      }
    });
    return flatArray;
  }

  setLinkValues(flowchartElements: FlowchartNodeType[], operationsService: OperationsService, link: joint.dia.Cell) {
    let linkInDto = null;
    const flatArray = this.flattenFlowchartElements(flowchartElements);

    flatArray.forEach((flowchartElement) => {
      if (flowchartElement?.outgoingLinks?.length > 0) {
        linkInDto = flowchartElement?.outgoingLinks.find((e) => e.id === link.id);
        if (linkInDto) {
          const sourceElement = findFlowchartElementRecursive(flowchartElements, linkInDto.fromFlowchartNodeId);
          if (sourceElement.elementType === FlowchartElementType.FlowchartDecisionNode) {
            const unit = this.sidebarService.getUnitFromSourceLinkDecision(linkInDto.fromFlowchartNodeId, flowchartElements);
            const onlyTruePaths = this.sidebarService.decisionNodeHasOnlyTruePaths(flowchartElement?.outgoingLinks);
            if (!onlyTruePaths) {
              operationsService.setLinkAttribute(linkInDto.attribute, linkInDto.id, unit);
            }
          } else {
            operationsService.setLinkAttribute(linkInDto.attribute, linkInDto.id);
          }
          operationsService.setLinkOffset((linkInDto as FlowchartLinkDto).linkOffset, (linkInDto as FlowchartLinkDto).id);
        }
      }
    });
  }

  setSubjectLinkLabelPosition(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.LinkView,
    linkLabelsDictionary: LinkLabelsDictionary,
  ): void {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const flowchartElement = this.sidebarService.deepSearchLinks(flowchartElements, element.model.id as string) as FlowchartLinkDto;

    if (flowchartElement?.attribute && linkLabelsDictionary?.attribute) {
      flowchartElement.attribute.position = linkLabelsDictionary.attribute;
    }

    if (flowchartElement?.linkOffset && linkLabelsDictionary?.offset) {
      flowchartElement.linkOffset.position = linkLabelsDictionary.offset;
    }

    flowchartElementsSubjectStore.next(flowchartElements);
  }

  setSubjectLinkVertices(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    linkId: string,
    linkVertices: LinkVertexDto[],
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const parentId = element.attributes.parent;

    if (parentId) {
      const parentElement = flowchartElements.find((el) => el.id === parentId);
      const foundElement = parentElement.children.find((el) => el.id === element.id);
      const link = foundElement.outgoingLinks.find((el) => el.id === linkId);
      link.linkVertices = linkVertices;
      flowchartElements = flowchartElements.map((item) => (item.id === parentId ? parentElement : item));
      flowchartElementsSubjectStore.next(flowchartElements);
    } else {
      const foundElement = flowchartElements.find((el) => el.id === element.id);
      const link = foundElement.outgoingLinks.find((el) => el.id === linkId);
      link.linkVertices = linkVertices;
      flowchartElements = flowchartElements.map((item) => (item.id === element.id ? foundElement : item));
      flowchartElementsSubjectStore.next(flowchartElements);
    }
  }

  setElementPositionInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.ElementView,
    paper?: joint.dia.Paper,
    graph?: joint.dia.Graph,
  ): void {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    if (!element?.model.attributes?.isContainerSubComponent) {
      if (element.model.isEmbedded() && !element.model.attributes?.isContainer) {
        const id = element.model.getParentCell().id;

        const flowchartElement = this.sidebarService.deepSearchElements(flowchartElements, id as string);

        flowchartElement?.children?.forEach((child) => {
          const childElement = child.id === element.model.id ? child : null;

          if (childElement) {
            const flowchartNodeParent = new FlowchartNode(flowchartElement);
            const flowchartNode = new FlowchartNode(childElement);
            flowchartNode.setPosition(element);
            flowchartElements = flowchartElements.map((item) => (item.id === id ? flowchartNodeParent.getElement() : item));
            flowchartElementsSubjectStore.next(flowchartElements);
          }
        });
      } else {
        const id = element.model.attributes.id;

        if (element.model.attributes?.isSubflow) {
          const subflowElement = this.sidebarService.deepSearchElements(flowchartElements, id);

          if (subflowElement) {
            const subflowNode = new FlowchartNode(subflowElement);
            subflowNode.setPosition(element);
            subflowNode.element.children?.forEach((sub) => {
              const subNode = new FlowchartNode(sub);
              const subElementModel = graph.getCell(sub.id);
              const subElement = paper.findViewByModel(subElementModel) as joint.dia.ElementView;
              if (subNode && subElement) {
                subNode.setPosition(subElement);
              }
            });

            flowchartElements = flowchartElements.map((item) => (item.id === id ? subflowNode.getElement() : item));
            flowchartElementsSubjectStore.next(flowchartElements);
          }
        } else {
          const flowchartElement = this.sidebarService.deepSearchElements(flowchartElements, id);

          if (flowchartElement) {
            const flowchartNode = new FlowchartNode(flowchartElement);
            flowchartNode.setPosition(element);
            flowchartElements = flowchartElements.map((item) => (item.id === id ? flowchartNode.getElement() : item));
            flowchartElementsSubjectStore.next(flowchartElements);
          }
        }
      }
    }
  }

  setElementSizeInSubject(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, element: joint.dia.ElementView): void {
    const id = element.model.attributes.id;
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const flowchartElement = this.sidebarService.deepSearchElements(flowchartElements, id);
    if (flowchartElement) {
      const flowchartNode = new FlowchartNode(flowchartElement);
      flowchartNode.setSize(element);
      flowchartElements = flowchartElements.map((item) => (item.id === id ? flowchartNode.getElement() : item));
      flowchartElementsSubjectStore.next(flowchartElements);
    }
  }

  setSelectionBehaviorValue(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    id: string,
    selectionBehaviorValue: SelectionBehavior,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    let dto = flowchartElements.filter((el) => el.id === id)[0];
    if (!dto) {
      flowchartElements.forEach((element) => {
        if (element?.children?.length > 0) {
          if (element.children.filter((el) => el.id === id)[0]) {
            dto = element.children.filter((el) => el.id === id)[0];
          }
        }
      });
    }

    if (dto.elementType === FlowchartElementType.FlowchartSelectionBehavior) {
      const selectionBehaviorDto = dto as FlowchartSelectionBehaviorDto;
      selectionBehaviorDto.selectionBehavior = selectionBehaviorValue;
    }
    if (dto.elementType === FlowchartElementType.FlowchartAction) {
      const flowchartActionDto = dto as FlowchartActionDto;
      if (flowchartActionDto.guidelineAction !== null) {
        flowchartActionDto.guidelineAction.selectionBehaviorForChildren = selectionBehaviorValue;
      }
    }

    //Workaround -> to be verified
    flowchartElements = flowchartElements.map((item) => (item.id === id ? dto : item));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  public addNodeToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    event: NodePickerEvent,
    newElement: joint.dia.Cell,
  ) {
    const dtoMap: Record<NodePickerEvent, () => FlowchartNode<FlowchartNodeType>> = {
      [NodePickerEvent.ADD_ACTION]: () => new FlowchartNode({ elementType: FlowchartElementType.FlowchartAction } as FlowchartActionDto),
      [NodePickerEvent.ADD_DECISION]: () =>
        new FlowchartNode({ elementType: FlowchartElementType.FlowchartDecisionNode } as FlowchartDecisionNodeDto),
      [NodePickerEvent.ADD_SEL_BEHAVIOR]: () =>
        new FlowchartNode({ elementType: FlowchartElementType.FlowchartSelectionBehavior } as FlowchartSelectionBehaviorDto),
      [NodePickerEvent.ADD_CONTAINER]: () => {
        const dto: FlowchartContainerDto = { elementType: FlowchartElementType.FlowchartContainer };
        dto.guidelineAction = {} as GuidelineActionDto;
        dto.guidelineAction.id = window.crypto.randomUUID();
        dto.guidelineAction.uniqueId = dto.guidelineAction.id;
        dto.guidelineAction.selectionBehaviorForChildren = SelectionBehavior.OneOrMore;
        dto.guidelineAction.componentType = GuidelineComponentType.Action;
        return new FlowchartNode(dto);
      },
      [NodePickerEvent.ADD_TEXT_BLOCK]: () =>
        new FlowchartNode({ elementType: FlowchartElementType.FlowchartTextBlock } as FlowchartTextBlockDto),
      [NodePickerEvent.ADD_SUBFLOW]: () => {
        const dto: FlowchartSubflowDto = { elementType: FlowchartElementType.FlowchartSubflow };
        dto.guidelineAction = {} as GuidelineActionDto;
        dto.guidelineAction.selectionBehaviorForChildren = SelectionBehavior.OneOrMore;
        dto.guidelineAction.componentType = GuidelineComponentType.Action;
        return new FlowchartNode(dto);
      },
    };

    const node = dtoMap[event]();
    node.setPropertiesForInit(newElement);
    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, node.getElement());
  }

  addStartNodeToSubject(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, element: joint.dia.Cell): void {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const startNode = new FlowchartNode({ elementType: FlowchartElementType.FlowchartStartNode } as FlowchartStartNodeDto);
    startNode.setPropertiesForInit(element);
    const startNodeDto = startNode.getElement();
    flowchartElements.push(startNodeDto);
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  addActionToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    const flowchartActionDto: FlowchartActionDto = {
      elementType: FlowchartElementType.FlowchartAction,
    };
    const actionNode = new FlowchartNode(flowchartActionDto);
    actionNode.setPropertiesForInit(element);
    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, actionNode.getElement(), link);
  }

  removeElementFromSubject(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, elementId: string) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const elementIndex = flowchartElements.findIndex((el) => el.id === elementId);

    if (elementIndex !== -1) {
      flowchartElements.splice(elementIndex, 1);
      flowchartElementsSubjectStore.next(flowchartElements);
    }
  }

  removeLinkFromSubject(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, link: joint.dia.Link) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const sourceElement = flowchartElements.find((el) => el.id === link.source().id);
    let subflowSourceElement;
    let subflow;

    if (!sourceElement) {
      flowchartElements.forEach((element) => {
        if (element?.children?.length > 0) {
          element.children.forEach((child) => {
            if (child.id === link.source().id) {
              subflowSourceElement = child;
              subflow = element;
            }
          });
        }
      });
    }

    if (sourceElement) {
      const linkToDelete = sourceElement.outgoingLinks.findIndex((el) => el.id === link.id);
      const sourceNode = new FlowchartNode(sourceElement);
      sourceNode.removeOutgoingLink(linkToDelete);
      flowchartElements = flowchartElements.map((item) => (item.id === sourceElement.id ? sourceNode.getElement() : item));
      flowchartElementsSubjectStore.next(flowchartElements);
    } else if (subflowSourceElement) {
      const linkToDelete = subflowSourceElement.outgoingLinks.findIndex((el) => el.id === link.id);
      const sourceNode = new FlowchartNode(subflowSourceElement);
      sourceNode.removeOutgoingLink(linkToDelete);

      if (subflow?.children?.length > 0) {
        subflow.children.map((child) => (child.id === subflowSourceElement.id ? sourceNode.getElement() : child));
      }

      flowchartElements = flowchartElements.map((item) => (item.id === subflow.id ? subflow : item));
      flowchartElementsSubjectStore.next(flowchartElements);
    }
  }

  removeElementInSubflowInSubject(nodeToRemove: joint.dia.Cell, flowchartElementsSubjectStore: FlowchartElementsSubjectStore) {
    const parentId = nodeToRemove.attributes.subflowParentId as string;
    this.removeElementFromParentInSubject(nodeToRemove, parentId, flowchartElementsSubjectStore);
    nodeToRemove.remove();
  }

  removeElementInContainerInSubject(
    nodeToRemove: joint.dia.Cell,
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    operationsService: OperationsService,
  ) {
    const parentId = nodeToRemove.attributes.parentContainerId as string;
    const container = this.sidebarService.deepSearchElements(flowchartElementsSubjectStore.getValue(), parentId);
    this.removeElementFromParentInSubject(nodeToRemove, parentId, flowchartElementsSubjectStore);
    nodeToRemove.remove();
    operationsService.updateSelectionBehaviorForSubActions(container as FlowchartActionDto);
  }

  removeElementFromParentInSubject(
    nodeToRemove: joint.dia.Cell,
    parentId: string,
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const parentElement = this.sidebarService.deepSearchElements(flowchartElements, parentId);
    parentElement.children = parentElement.children.filter((el) => el.id !== nodeToRemove.attributes.id);
    flowchartElements = flowchartElements.map((el) => (el.id === parentId ? parentElement : el));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  public exchangeContainerSubElementsInSubject(
    elementId: string,
    exchangeId: string,
    parentId: string,
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    operationsService: OperationsService,
  ) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const container = this.sidebarService.deepSearchElements(flowchartElements, parentId);
    const subElements = container.children;
    const elementIndex = subElements.findIndex((el) => el.id === elementId);
    const newIndex = subElements.findIndex((el) => el.id === exchangeId);

    if (elementIndex < 0 || elementIndex >= subElements.length || newIndex < 0 || newIndex >= subElements.length) {
      return;
    }

    operationsService.exchangeEmbeddedOrder(parentId, elementId, exchangeId);

    [subElements[elementIndex], subElements[newIndex]] = [subElements[newIndex], subElements[elementIndex]];
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  removeElementAndAdjustLinksInSubject(
    nodeToRemove: joint.dia.Cell,
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    operationsService: OperationsService,
  ) {
    const attributes = nodeToRemove.attributes;
    if (attributes.parentContainerId) {
      this.removeElementInContainerInSubject(nodeToRemove, flowchartElementsSubjectStore, operationsService);
    } else if (attributes.subflowParentId) {
      this.removeElementInSubflowInSubject(nodeToRemove, flowchartElementsSubjectStore);
      const newLinks = operationsService.recreateLinks(nodeToRemove);
      this.recreateLinksInSubject(flowchartElementsSubjectStore, nodeToRemove.id, newLinks);
    } else {
      const newLinks = operationsService.recreateLinks(nodeToRemove);
      this.recreateLinksInSubject(flowchartElementsSubjectStore, nodeToRemove.id, newLinks);
    }
  }

  removeElement(
    nodeToRemove: joint.dia.Cell,
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    operationsService: OperationsService,
  ) {
    const attributes = nodeToRemove.attributes;
    if (attributes.parentContainerId) {
      this.removeElementInContainerInSubject(nodeToRemove, flowchartElementsSubjectStore, operationsService);
    } else if (attributes.subflowParentId) {
      this.removeElementInSubflowInSubject(nodeToRemove, flowchartElementsSubjectStore);
    } else {
      this.removeElementFromSubject(flowchartElementsSubjectStore, nodeToRemove.attributes.id);
    }
  }

  //Refactor
  recreateLinksInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    elementId: joint.dia.Cell.ID,
    newLinks: joint.dia.Cell[],
  ) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const flattenedArray = this.flattenFlowchartElements(flowchartElements as Partial<FlowchartNodeDto>[]);

    //remove links to delete node
    flattenedArray.forEach((el) => {
      el.outgoingLinks?.forEach((link, index) => {
        if (link.toFlowchartNodeId === elementId) {
          el.outgoingLinks.splice(index, 1);
        }
      });
    });
    //add new links
    newLinks.forEach((link: joint.shapes.standard.Link) => {
      const elementOut = flattenedArray.find((el) => el.id === link.attributes.source.id);
      const linkId = link.id;
      const linkSourceId = link.attributes.source.id;
      const linkTargetId = link.attributes.target.id;
      const sourceAnchor = this.converterService.getAnchorNumber(link.source().anchor?.name as joint.dia.PositionName);
      const targetAnchor = this.converterService.getAnchorNumber(link.target().anchor?.name as joint.dia.PositionName);
      const newLink = this.converterService.recreateNewLinkDto(sourceAnchor, targetAnchor, linkId, linkSourceId, linkTargetId);
      elementOut.outgoingLinks.push(newLink);
    });

    //update link stuff
    flowchartElementsSubjectStore.next(flowchartElements);
    //remove element from from only if root level - ToDo Refactor
    if (flowchartElementsSubjectStore.getValue().find((el) => el.id === elementId)) {
      this.removeElementFromSubject(flowchartElementsSubjectStore, elementId as string);
    }
  }

  addDecisionToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    const decisionNode = new FlowchartNode({
      elementType: FlowchartElementType.FlowchartDecisionNode,
    } as FlowchartDecisionNodeDto);
    decisionNode.setPropertiesForInit(element);
    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, decisionNode.getElement(), link);
  }

  addContainerToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    const flowchartContainerDto: FlowchartContainerDto = {
      elementType: FlowchartElementType.FlowchartContainer,
    };
    flowchartContainerDto.guidelineAction = {};
    flowchartContainerDto.guidelineAction.id = window.crypto.randomUUID();
    flowchartContainerDto.guidelineAction.uniqueId = flowchartContainerDto.guidelineAction.id;
    flowchartContainerDto.guidelineAction.selectionBehaviorForChildren = SelectionBehavior.OneOrMore;
    flowchartContainerDto.guidelineAction.componentType = GuidelineComponentType.Action;

    const containerNode = new FlowchartNode(flowchartContainerDto);
    containerNode.setPropertiesForInit(element);
    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, containerNode.getElement(), link);
  }

  addSubComponentToContainerInSubject(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, element: joint.dia.Cell, _cellView?) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const containerFirstLevel = flowchartElements.find((el) => el.id === element.attributes.parentContainerId) as FlowchartActionDto;
    if (containerFirstLevel) {
      this.assignSubComponentToSubject(flowchartElementsSubjectStore, element, containerFirstLevel);
    } else {
      let containerSecondLevel;
      flowchartElements.forEach((el) => {
        if (el.id === _cellView.model.attributes.subflowParentId) {
          const containerAction = el as FlowchartActionDto;
          containerAction.children.forEach((child) => {
            if (child.id === _cellView.model.attributes.id) {
              containerSecondLevel = child;
            }
          });
        }
      });

      this.assignSubComponentToSubject(flowchartElementsSubjectStore, element, containerSecondLevel as FlowchartActionDto);
    }
  }

  assignSubComponentToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    myContainer: FlowchartActionDto,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    let subContainerNode;
    if (element.attributes.isContainerSubAction) {
      const subActionDto: FlowchartActionDto = { elementType: FlowchartElementType.FlowchartAction };
      subContainerNode = new FlowchartNode(subActionDto);
    } else if (element.attributes.isContainerSubDecision) {
      const subDecisionDto: FlowchartDecisionNodeDto = {
        elementType: FlowchartElementType.FlowchartDecisionNode,
      };
      subContainerNode = new FlowchartNode(subDecisionDto);
    }
    subContainerNode.setPropertiesForInit(element);

    if (!myContainer.children) {
      myContainer.children = [subContainerNode.getElement()];
    } else {
      myContainer.children.push(subContainerNode.getElement());
    }

    flowchartElements = flowchartElements.map((item) => this.updateContainer(item, element.attributes.parentContainerId, myContainer));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  updateContainer(item, parentContainerId, newContainer) {
    if (item.id === parentContainerId) {
      return newContainer;
    }

    if (item.children && item.children.length > 0) {
      item.children = item.children.map((child) => this.updateContainer(child, parentContainerId, newContainer));
    }

    return item;
  }

  addSubflowToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    const flowchartSubflowDto: FlowchartSubflowDto = {
      elementType: FlowchartElementType.FlowchartSubflow,
    };
    flowchartSubflowDto.guidelineAction = {} as GuidelineActionDto;
    flowchartSubflowDto.guidelineAction.selectionBehaviorForChildren = SelectionBehavior.OneOrMore;
    flowchartSubflowDto.guidelineAction.componentType = GuidelineComponentType.Action;
    const subflowNode = new FlowchartNode(flowchartSubflowDto);
    subflowNode.setPropertiesForInit(element);

    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, subflowNode.getElement(), link);
  }

  addChildActionToSubflowInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    element.set('subflowParentId', element.attributes.parent);
    const subflow = flowchartElements.find((el) => el.id === element.attributes.subflowParentId);
    const actionDto: FlowchartActionDto = {
      elementType: FlowchartElementType.FlowchartAction,
    };
    const actionNode = new FlowchartNode(actionDto);
    actionNode.setPropertiesForInit(element);
    if (!subflow.children) {
      subflow.children = [actionNode.getElement()];
    } else {
      subflow.children.push(actionNode.getElement());
    }

    if (subflow && subflow.children.length > 0 && link) {
      this.assignOutgoingLinks(subflow.children, link);
    }

    flowchartElements = flowchartElements.map((item) => (item.id === element.attributes.subflowParentId ? subflow : item));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  addChildDecisionToSubflowInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    element.set('subflowParentId', element.attributes.parent);
    const subflow: FlowchartDecisionNodeDto = flowchartElements.find((el) => el.id === element.attributes.subflowParentId);

    const newFlowchartDecisionNodeDto: FlowchartDecisionNodeDto = {
      elementType: FlowchartElementType.FlowchartDecisionNode,
    };

    const decisionNode = new FlowchartNode(newFlowchartDecisionNodeDto);
    decisionNode.setPropertiesForInit(element);

    if (!subflow.children) {
      subflow.children = [decisionNode.getElement()];
    } else {
      subflow.children.push(decisionNode.getElement());
    }

    if (subflow?.children.length > 0 && link) {
      this.assignOutgoingLinks(subflow.children, link);
    }

    flowchartElements = flowchartElements.map((item) => (item.id === element.attributes.subflowParentId ? subflow : item));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  addChildSelectionBehaviorToSubflowInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();

    element.set('subflowParentId', element.attributes.parent);
    const subflow = flowchartElements.find((el) => el.id === element.attributes.subflowParentId);

    const newFlowchartSelectionBehaviorDto: FlowchartSelectionBehaviorDto = {
      elementType: FlowchartElementType.FlowchartSelectionBehavior,
    };

    const selectionBehaviorNode = new FlowchartNode(newFlowchartSelectionBehaviorDto);
    selectionBehaviorNode.setPropertiesForInit(element);
    const mySubflow = subflow as FlowchartSelectionBehaviorDto;
    if (!mySubflow.children) {
      mySubflow.children = [selectionBehaviorNode.getElement()];
    } else {
      mySubflow.children.push(selectionBehaviorNode.getElement());
    }

    if (mySubflow && mySubflow.children.length > 0 && link) {
      this.assignOutgoingLinks(mySubflow.children, link);
    }

    flowchartElements = flowchartElements.map((item) => (item.id === element.attributes.subflowParentId ? mySubflow : item));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  addChildContainerToSubflowInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    element.set('subflowParentId', element.attributes.parent);
    const subflow = flowchartElements.find((el) => el.id === element.attributes.subflowParentId);
    const flowchartContainerDto: FlowchartContainerDto = {
      elementType: FlowchartElementType.FlowchartContainer,
    };

    flowchartContainerDto.guidelineAction = {} as GuidelineActionDto;
    flowchartContainerDto.guidelineAction.id = window.crypto.randomUUID();
    flowchartContainerDto.guidelineAction.uniqueId = flowchartContainerDto.guidelineAction.id;
    flowchartContainerDto.guidelineAction.selectionBehaviorForChildren = SelectionBehavior.OneOrMore;
    flowchartContainerDto.guidelineAction.componentType = GuidelineComponentType.Action;
    const containerNode = new FlowchartNode(flowchartContainerDto);
    containerNode.setPropertiesForInit(element);
    const mySubflow = subflow as FlowchartSelectionBehaviorDto;
    if (!mySubflow.children) {
      mySubflow.children = [containerNode.getElement()];
    } else {
      mySubflow.children.push(containerNode.getElement());
    }

    if (mySubflow && mySubflow.children.length > 0 && link) {
      this.assignOutgoingLinks(mySubflow.children, link);
    }

    flowchartElements = flowchartElements.map((item) => (item.id === element.attributes.subflowParentId ? mySubflow : item));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  addChildTextBlockToSubflowInSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    let flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    element.set('subflowParentId', element.attributes.parent);
    const subflow = flowchartElements.find((el) => el.id === element.attributes.subflowParentId);

    const newFlowchartTextBlockDto: FlowchartTextBlockDto = {
      elementType: FlowchartElementType.FlowchartTextBlock,
    };
    const textBlockNode = new FlowchartNode(newFlowchartTextBlockDto);
    textBlockNode.setPropertiesForInit(element);
    const mySubflow = subflow as FlowchartTextBlockDto;
    if (!mySubflow.children) {
      mySubflow.children = [textBlockNode.getElement()];
    } else {
      mySubflow.children.push(textBlockNode.getElement());
    }

    if (mySubflow && mySubflow.children.length > 0 && link) {
      this.assignOutgoingLinks(mySubflow.children, link);
    }

    flowchartElements = flowchartElements.map((item) => (item.id === element.attributes.subflowParentId ? mySubflow : item));
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  addFlowchartNodeToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    nodeDto: Partial<FlowchartDecisionNodeDto> & Partial<FlowchartActionDto>,
    link?: FlowchartLinkDto,
  ) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    this.assignOutgoingLinks(flowchartElements, link);
    flowchartElements.push(nodeDto);
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  assignOutgoingLinks(elements: any, link?: FlowchartLinkDto): void {
    if (!elements || elements.length === 0 || !link) return;
    const elementsValue = elements?.value || elements;
    elementsValue.forEach((element) => {
      if (link.fromFlowchartNodeId === element.id) {
        const node = element as FlowchartNodeDto;
        node.outgoingLinks = node.outgoingLinks || [];
        node.outgoingLinks.push(link);
      }
    });
  }

  addSelectionBehaviorToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    const flowchartSelectionBehaviorDto: FlowchartSelectionBehaviorDto = {
      elementType: FlowchartElementType.FlowchartSelectionBehavior,
    };
    const selectionBehavior = new FlowchartNode(flowchartSelectionBehaviorDto);
    selectionBehavior.setPropertiesForInit(element);
    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, selectionBehavior.getElement(), link);
  }

  addTextBlockToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    element: joint.dia.Cell,
    link: FlowchartLinkDto | undefined,
  ) {
    const flowchartTextBlockNode: FlowchartTextBlockDto = {
      elementType: FlowchartElementType.FlowchartTextBlock,
    };

    const textBlockNode = new FlowchartNode(flowchartTextBlockNode);
    textBlockNode.setPropertiesForInit(element);
    this.addFlowchartNodeToSubject(flowchartElementsSubjectStore, textBlockNode.getElement(), link);
  }

  addLinkToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    sourceElement: joint.dia.ElementView,
    link: joint.dia.Link,
  ) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const sourcePort = link.source().magnet;
    const targetPort = link.target().magnet;
    let getLinkPortSource;
    let getLinkPortTarget;

    if (link.source().magnet && link.target().magnet) {
      getLinkPortSource = this.getLinkAnchorPort(sourcePort);
      getLinkPortTarget = this.getLinkAnchorPort(targetPort);
    } else {
      getLinkPortSource = this.converterService.getAnchorNumber(link.source().anchor?.name as joint.dia.PositionName);
      getLinkPortTarget = this.converterService.getAnchorNumber(link.target().anchor?.name as joint.dia.PositionName);
    }

    const linkSource: LinkAnchorData = this.converterService.getAnchorName(getLinkPortSource);
    const linkTarget: LinkAnchorData = this.converterService.getAnchorName(getLinkPortTarget);

    const linkDto = this.converterService.convertFlowchartLinkToLinkDto(
      sourceElement.model.id as string,
      link.getTargetElement().id as string,
      linkSource,
      linkTarget,
      link.id as string,
    );

    if (!sourceElement.model.attributes?.parent) {
      const fromNodeDto = flowchartElements.find((el) => el.id === linkDto.fromFlowchartNodeId);
      fromNodeDto.outgoingLinks = fromNodeDto?.outgoingLinks?.length > 0 ? fromNodeDto.outgoingLinks : [];
      const existingLink = fromNodeDto.outgoingLinks.find((l) => l.id === link.id);
      if (existingLink === null || existingLink === undefined) {
        fromNodeDto.outgoingLinks.push(linkDto);
      }

      flowchartElementsSubjectStore.next(flowchartElements);
    } else {
      const subflow = flowchartElements.find((el) => el.id === sourceElement.model.attributes?.parent);
      const fromNodeDto = subflow.children.find((el) => el.id === linkDto.fromFlowchartNodeId);
      fromNodeDto.outgoingLinks = fromNodeDto?.outgoingLinks?.length > 0 ? fromNodeDto.outgoingLinks : [];
      const existingLink = fromNodeDto.outgoingLinks.find((l) => l.id === link.id);

      if (existingLink === null || existingLink === undefined) {
        flowchartElements.forEach((el, index) => {
          const childIndex = subflow.children.findIndex((child) => child.id === fromNodeDto.id);
          if (el.id === subflow.id && childIndex) {
            flowchartElements[index].children[childIndex].outgoingLinks.push(linkDto);
          }
        });
      }

      flowchartElementsSubjectStore.next(flowchartElements);
    }
  }

  addPortLinkToSubject(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    sourceElement: joint.dia.ElementView,
    link: joint.dia.Link,
  ) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();

    const linkDto = this.converterService.convertFlowchartLinkToLinkDto(
      sourceElement.model.id as string,
      link.getTargetElement().id as string,
      link.source().anchor as LinkAnchorData,
      link.target().anchor as LinkAnchorData,
      link.id as string,
    );

    const fromNodeDto = this.sidebarService.deepSearchElements(flowchartElements, linkDto.fromFlowchartNodeId);
    fromNodeDto.outgoingLinks = fromNodeDto?.outgoingLinks?.length > 0 ? fromNodeDto.outgoingLinks : [];
    const existingLink = fromNodeDto.outgoingLinks.find((l) => l.id === link.id);
    if (existingLink === null || existingLink === undefined) {
      fromNodeDto.outgoingLinks.push(linkDto);
    }

    flowchartElementsSubjectStore.next(flowchartElements);
  }

  setLinkAnchor(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, id: string, anchorName: string, anchorType: string) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const link: FlowchartLinkDto = this.sidebarService.deepSearchLinks(flowchartElements, id) as FlowchartLinkDto;
    if (link) {
      if (anchorType === 'source') {
        link.fromFlowchartNodeAnchor = this.getLinkAnchorByName(anchorName);
      } else {
        link.toFlowchartNodeAnchor = this.getLinkAnchorByName(anchorName);
      }
    }

    flowchartElementsSubjectStore.next(flowchartElements);
  }

  setManualLink(flowchartElementsSubjectStore: FlowchartElementsSubjectStore, id: string, isManual: boolean) {
    const flowchartElements = flowchartElementsSubjectStore.getCloneDeepValue();
    const link: FlowchartLinkDto = this.sidebarService.deepSearchLinks(flowchartElements, id);
    link.linkVerticesConfig = { isManual: isManual };
    flowchartElementsSubjectStore.next(flowchartElements);
  }

  getLinkAnchorByName(anchorName: string) {
    switch (anchorName) {
      case 'left':
        return LinkAnchor.Left;
      case 'right':
        return LinkAnchor.Right;
      case 'top':
        return LinkAnchor.Top;
      case 'bottom':
        return LinkAnchor.Bottom;
    }
  }

  private getLinkAnchorPort(port: string): LinkAnchor {
    switch (port) {
      case 'portLeft':
        return LinkAnchor.Left;
      case 'portRight':
        return LinkAnchor.Right;
      case 'portTop':
        return LinkAnchor.Top;
      case 'portBottom':
        return LinkAnchor.Bottom;
    }
  }
}
